// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { DepositAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Deposit/module';
import FormDialog from '../../../components/Dialog/FormDialog';
import useMutate from '../../../hooks/mutations/useMutate';
import useBridgeUserAgreement from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreement';
import TemplateContract from '../../../utils/TemplateContract';
import { SUC_MINT_HOLDING, ERR_MINT_HOLDING } from '../../../utils/strings';
import useMintFromAttestation from '../../../hooks/mutations/bridge/deposits/useMintFromAttestation';
import { useSnackbar } from 'notistack';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  attestations: TemplateContract<DepositAttestation>[];
};

export const MintFromAttestationDialog = ({ open, onClose, attestations }: DialogProps) => {
  const [label, setLabel] = useState<string>('');
  const bridgeUserAgreement = useBridgeUserAgreement();
  const mintFromAttestation = useMintFromAttestation();
  const mutate = useMutate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { showBoundary } = useErrorBoundary<Error>();
  const holdingTotal = attestations.reduce(
    (acc, holding) => acc + parseFloat(holding.payload.amount),
    0,
  );

  const resetState = useCallback(() => {
    setLabel('');
  }, [setLabel]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);
  const handleSubmit = useCallback(async () => {
    if (!bridgeUserAgreement.data) {
      showBoundary(
        new Error(ERR_MINT_HOLDING, { cause: 'Bridge user agreement is not available.' }),
      );
      return;
    }
    for (const attestationElement of attestations) {
      const payload = {
        bridgeUserAgreementCid: bridgeUserAgreement.data.contractId,
        depositAttestation: attestationElement,
      };
      const result = await mutate(mintFromAttestation, payload, SUC_MINT_HOLDING, ERR_MINT_HOLDING);
      if (result) {
        const updateId = result[1];
        enqueueSnackbar(`Update ID: ${updateId}`, {
          variant: 'success',
          autoHideDuration: 10000,
          action: (key) => (
            <button
              onClick={async () => {
                await navigator.clipboard.writeText(updateId);
                enqueueSnackbar('Update ID copied to clipboard', { variant: 'success' });
                closeSnackbar(key);
              }}
              style={{
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              Copy
            </button>
          ),
        });
      }
    }
    handleClose();
  }, [bridgeUserAgreement, mintFromAttestation, attestations, label, handleClose, mutate, showBoundary]);

  const { isLoading } = bridgeUserAgreement;
  if (isLoading) return null;

  return (
    <FormDialog
      open={open}
      title="Mint USDCx"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Mint"
    >
      <>Mint {holdingTotal} USDCx on Canton Network</>
    </FormDialog>
  );
};
